import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { graphql, useStaticQuery } from 'gatsby'
import { useIsSSR } from 'utils/isSSR'
import PlayIcon from './play.svg'
import PauseIcon from './pause.svg'

const SHero = styled.div`
  position: relative;
  height: 100%;

  video {
    position: absolute;

    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    width: 100%;
    height: 100%;

    object-fit: cover;
    object-position: center;

    z-index: 0;
  }
`

const PlayButton = styled.button<{
  $playing?: boolean
}>`
  display: block;
  appearance: none;
  border: none;
  outline: none;

  position: absolute;
  top: 1rem;
  right: 1rem;

  width: 3rem;
  height: 3rem;

  background: transparent;

  z-index: 10;

  // props => props.$playing

  &:before,
  &:after {
    content: '';

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    mask-size: contain;
    mask-repeat: no-repeat;
    background-color: #fff;
    opacity: 0.8;
  }

  &:before {
    display: ${(props) => (props.$playing ? 'none' : 'block')};
    mask-image: url('${PlayIcon}');
  }

  &:after {
    display: ${(props) => (props.$playing ? 'block' : 'none')};
    mask-image: url('${PauseIcon}');
  }
`

const Hero: React.FC = () => {
  const { mp4, webm } = useStaticQuery<Queries.HeroBackgroundQuery>(graphql`
    fragment HeroVideo on File {
      id
      publicURL
    }
    
    query HeroBackground {
      mp4: file(relativePath: { eq: "natural/homepage-hero-optim.mp4" }) {
        ...HeroVideo
      }
      webm: file(relativePath: { eq: "natural/homepage-hero-optim.webm" }) {
        ...HeroVideo
      }
    }
  `)

  const ref = useRef<IntersectionObserver>()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [loaded, setLoaded] = useState(false)
  const [videoActive, setVideoActive] = useState(false)
  const [allowVideo, setAllowVideo] = useState(true)
  const [isVideoPlaying, setIsVideoPlaying] = useState(true)
  const isSSR = useIsSSR()

  useEffect(() => {
    if (isSSR) {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const cb = () => {
      if (!mediaQuery.matches) {
        return
      }

      setAllowVideo(false)
      setIsVideoPlaying(false)
    }
    cb()
    mediaQuery.addEventListener('change', cb)

    return () => {
      mediaQuery.removeEventListener('change', cb)
    }
  }, [isSSR])

  const handleRef = useCallback(
    (elem: HTMLDivElement | null) => {
      if (ref.current) {
        ref.current?.disconnect()
      }

      if (loaded || !elem || !('IntersectionObserver' in window)) {
        return
      }

      ref.current = new IntersectionObserver(function (entries) {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue
          }

          setLoaded(true)
        }
      })

      ref.current?.observe(elem)
    },
    [loaded]
  )

  const videos = useMemo(() => {
    if (!webm || !mp4) {
      return []
    }

    return [
      {
        id: `${webm.id}`,
        src: webm.publicURL ?? undefined,
        type: 'video/webm',
      },
      {
        id: `${mp4.id}-mp4`,
        src: mp4.publicURL ?? undefined,
        type: 'video/mp4',
      },
    ].filter(({ src }) => !!src)
  }, [webm, mp4])

  useEffect(() => {
    if (isSSR) {
      return
    }

    const checkWindowSize = () => {
      setVideoActive((a) => a || window.innerWidth > 764)
    }

    window.addEventListener('resize', checkWindowSize)
    checkWindowSize()

    return () => {
      window.removeEventListener('resize', checkWindowSize)
    }
  }, [isSSR])

  useLayoutEffect(() => {
    if (!loaded) {
      return
    }

    const f = requestAnimationFrame(() => videoRef.current?.load())

    return () => {
      cancelAnimationFrame(f)
    }
  }, [loaded])

  const onChangePlay = useCallback(() => {
    if (!videoRef.current) {
      return
    }

    setIsVideoPlaying((play) => !play)
    if (videoRef.current.paused) {
      videoRef.current.play()
      return
    }

    videoRef.current.pause()
  }, [])

  const onEnded = useCallback<React.ReactEventHandler<HTMLVideoElement>>((event) => {
    event.currentTarget.pause()
    event.currentTarget.currentTime = 0;
    void event.currentTarget.play()
  }, [])

  return (
    <SHero ref={handleRef}>
      {videoActive && (
        <>
          <PlayButton $playing={isVideoPlaying} onClick={onChangePlay}>
            <span hidden aria-hidden={false}>
              {!isVideoPlaying && 'Play Video'}
              {isVideoPlaying && 'Stop Video'}
            </span>
          </PlayButton>
          <video ref={videoRef} autoPlay={allowVideo} onEnded={onEnded} muted loop playsInline>
            {videos.map((video) => (
              <source
                key={video.id}
                src={loaded ? video.src : undefined}
                type={video.type}
              />
            ))}
          </video>
        </>
      )}
    </SHero>
  )
}

export default Hero
